'use client';

import { useEffect, useState, useTransition } from 'react';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { X, Cookie, Settings } from 'lucide-react';
import {
  useConsent,
  setConsent as persistConsent,
  acceptAllConsent,
  rejectAllConsent,
} from '@/lib/consent-client';
import { CONSENT_VERSION, type ConsentState } from '@/lib/consent';
import { logConsentChange } from '@/actions/privacy/log-consent';
import { cn } from '@/lib/utils';
import type { Locale } from '@/lib/i18n/routing';

// Strings inline (no van por messages JSON porque este componente puede
// renderizar antes de que el provider hidrate la cookie).
const COPY = {
  'es-MX': {
    region: 'Aviso de cookies',
    body: 'Usamos cookies para hacer que NQORO funcione. Las esenciales mantienen tu sesión y carrito. Las de analítica y marketing solo se activan si tú las aceptas.',
    fineprint: 'Puedes cambiar tus preferencias cuando quieras desde el footer o en',
    customize: 'Personalizar',
    rejectAll: 'Solo necesarias',
    acceptAll: 'Aceptar todo',
    modalEyebrow: 'Preferencias',
    modalTitle: 'Cookies y tecnologías similares',
    modalBody:
      'Tu privacidad importa. Activa solo las categorías que quieras. Puedes cambiar esto cuando sea desde el footer.',
    necessary: 'Necesarias',
    necessaryDesc:
      'Sesión, carrito, seguridad CSRF, preferencias de idioma. Sin estas el sitio no funciona.',
    preferences: 'Preferencias',
    preferencesDesc:
      'Guardan elecciones no esenciales como tema visual y detecciones de idioma específicas.',
    analytics: 'Analítica',
    analyticsDesc:
      'Nos ayudan a entender qué partes del sitio funcionan. Datos agregados, no identifican por nombre.',
    marketing: 'Marketing',
    marketingDesc:
      'Permiten medir efectividad de campañas en redes y enviarte comunicación relevante. Incluye píxeles de Meta y TikTok cuando estén activos.',
    alwaysOn: 'Siempre activas',
    privacyPrefix: 'Al aceptar aceptas nuestro',
    privacyLink: 'aviso de privacidad',
    privacyAnd: 'y',
    cookieLink: 'política de cookies',
    arcoPrefix: 'Ejerce tus derechos ARCO en',
    save: 'Guardar mis elecciones',
    close: 'Cerrar',
  },
  'en-US': {
    region: 'Cookie notice',
    body: 'We use cookies to keep NQORO running. Essentials keep your session and cart. Analytics and marketing cookies only activate if you accept them.',
    fineprint: 'You can change your preferences anytime from the footer or at',
    customize: 'Customise',
    rejectAll: 'Essentials only',
    acceptAll: 'Accept all',
    modalEyebrow: 'Preferences',
    modalTitle: 'Cookies and similar technologies',
    modalBody:
      'Your privacy matters. Enable only the categories you want. You can change this whenever from the footer.',
    necessary: 'Necessary',
    necessaryDesc:
      'Session, cart, CSRF security, language preferences. Without these the site does not work.',
    preferences: 'Preferences',
    preferencesDesc:
      'Save non-essential choices like visual theme and specific language detections.',
    analytics: 'Analytics',
    analyticsDesc:
      'Help us understand which parts of the site work. Aggregate data, not name-identifiable.',
    marketing: 'Marketing',
    marketingDesc:
      'Allow us to measure social campaign effectiveness and send you relevant communication. Includes Meta and TikTok pixels when active.',
    alwaysOn: 'Always on',
    privacyPrefix: 'By accepting you accept our',
    privacyLink: 'privacy notice',
    privacyAnd: 'and',
    cookieLink: 'cookie policy',
    arcoPrefix: 'Exercise your ARCO rights at',
    save: 'Save my choices',
    close: 'Close',
  },
} as const;

/**
 * Banner CMP NQORO.
 *
 * Aparece en la parte inferior cuando el usuario **no ha decidido** todavía
 * (cookie `nqoro_consent.decidedAt === null`). 3 CTAs:
 *   - Aceptar todo
 *   - Solo necesarias (reject all)
 *   - Personalizar → abre modal con toggles por categoría
 *
 * Modal también disponible desde el footer (link "Preferencias de cookies")
 * via `window.dispatchEvent(new Event('nqoro:openCmp'))`.
 *
 * Accessibility:
 *   - role="dialog" + aria-modal en el modal
 *   - Focus trap en el modal (primer botón recibe focus)
 *   - Tecla ESC cierra el modal
 *   - Banner NO atrapa focus (es informativo, no modal)
 *   - Contraste AA sobre fondo noir
 */
export function ConsentBanner() {
  const locale = useLocale() as Locale;
  const c = COPY[locale] ?? COPY['es-MX'];
  const { consent, hasDecided } = useConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Montar con delay para evitar flicker en SSR hydration
  useEffect(() => {
    if (!hasDecided) {
      const t = setTimeout(() => setShowBanner(true), 600);
      return () => clearTimeout(t);
    }
    setShowBanner(false);
    return;
  }, [hasDecided]);

  // Listener global para reabrir desde footer
  useEffect(() => {
    function openCmp() {
      setShowModal(true);
    }
    window.addEventListener('nqoro:openCmp', openCmp);
    return () => window.removeEventListener('nqoro:openCmp', openCmp);
  }, []);

  function handleAcceptAll() {
    const state = acceptAllConsent();
    void logConsentChange({
      action: 'accept_all',
      categories: normalize(state),
      consentVersion: String(CONSENT_VERSION),
    });
    setShowBanner(false);
    setShowModal(false);
  }

  function handleRejectAll() {
    const state = rejectAllConsent();
    void logConsentChange({
      action: 'reject_all',
      categories: normalize(state),
      consentVersion: String(CONSENT_VERSION),
    });
    setShowBanner(false);
    setShowModal(false);
  }

  function handleSaveCustom(categories: {
    preferences: boolean;
    analytics: boolean;
    marketing: boolean;
  }) {
    const state = persistConsent(categories);
    void logConsentChange({
      action: 'customize',
      categories: normalize(state),
      consentVersion: String(CONSENT_VERSION),
    });
    setShowBanner(false);
    setShowModal(false);
  }

  if (!showBanner && !showModal) return null;

  return (
    <>
      {showBanner && !showModal ? (
        <div
          role="region"
          aria-label={c.region}
          className="fixed inset-x-0 bottom-0 z-[var(--z-toast,200)] border-t border-[var(--color-accent)]/30 bg-[var(--color-surface)]/95 backdrop-blur-md"
        >
          <div className="container-body flex flex-col gap-4 py-5 md:flex-row md:items-center md:gap-6 md:py-6">
            <div className="flex items-start gap-3 md:flex-1">
              <Cookie
                className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-accent)]"
                strokeWidth={1.5}
              />
              <div>
                <p className="type-body-sm text-[var(--color-fg)]">{c.body}</p>
                <p className="type-caption mt-1 text-[var(--color-fg-muted)]">
                  {c.fineprint}{' '}
                  <Link
                    href="/legal/cookies"
                    className="underline hover:text-[var(--color-accent)]"
                  >
                    /legal/cookies
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="type-nav inline-flex h-10 items-center gap-2 rounded-sm border border-[var(--color-border-strong)] px-4 text-[var(--color-fg)] hover:border-[var(--color-accent)]"
              >
                <Settings className="h-4 w-4" />
                {c.customize}
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                className="type-nav inline-flex h-10 items-center rounded-sm border border-[var(--color-border-strong)] px-4 text-[var(--color-fg)] hover:border-[var(--color-accent)]"
              >
                {c.rejectAll}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="type-nav inline-flex h-10 items-center rounded-sm bg-[var(--color-accent)] px-4 text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]"
              >
                {c.acceptAll}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showModal ? (
        <ConsentPreferencesModal
          initial={consent}
          c={c}
          onClose={() => {
            if (!hasDecided) setShowBanner(true);
            setShowModal(false);
          }}
          onSave={handleSaveCustom}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
        />
      ) : null}
    </>
  );
}

function normalize(state: ConsentState) {
  return {
    necessary: true as const,
    preferences: state.preferences,
    analytics: state.analytics,
    marketing: state.marketing,
  };
}

type CopyMap = Record<keyof (typeof COPY)['es-MX'], string>;

interface ModalProps {
  initial: ConsentState;
  c: CopyMap;
  onClose: () => void;
  onSave: (categories: { preferences: boolean; analytics: boolean; marketing: boolean }) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
}

function ConsentPreferencesModal({
  initial,
  c,
  onClose,
  onSave,
  onAcceptAll,
  onRejectAll,
}: ModalProps) {
  const [preferences, setPreferences] = useState(initial.preferences);
  const [analytics, setAnalytics] = useState(initial.analytics);
  const [marketing, setMarketing] = useState(initial.marketing);
  const [, startTransition] = useTransition();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function save() {
    startTransition(() => onSave({ preferences, analytics, marketing }));
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmp-title"
      className="fixed inset-0 z-[var(--z-modal,300)] flex items-end justify-center bg-black/60 p-4 md:items-center"
    >
      <div className="relative w-full max-w-2xl overflow-hidden rounded-sm border border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl">
        <div className="flex items-start justify-between border-b border-[var(--color-border)] p-6">
          <div>
            <p className="type-eyebrow text-[var(--color-fg-subtle)]">{c.modalEyebrow}</p>
            <h2 id="cmp-title" className="type-h3 mt-1 text-[var(--color-fg)]">
              {c.modalTitle}
            </h2>
            <p className="type-body-sm mt-3 text-[var(--color-fg-muted)]">{c.modalBody}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={c.close}
            className="type-nav -mt-2 -mr-2 p-2 text-[var(--color-fg-muted)] hover:text-[var(--color-accent)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto p-6">
          <CategoryToggle
            title={c.necessary}
            description={c.necessaryDesc}
            examples={['authjs.session-token', 'nqoro-guest-id', 'nqoro_consent']}
            alwaysOnLabel={c.alwaysOn}
            checked={true}
            disabled
            onChange={() => {}}
          />
          <CategoryToggle
            title={c.preferences}
            description={c.preferencesDesc}
            examples={['NEXT_LOCALE']}
            alwaysOnLabel={c.alwaysOn}
            checked={preferences}
            onChange={setPreferences}
          />
          <CategoryToggle
            title={c.analytics}
            description={c.analyticsDesc}
            examples={['Google Analytics 4', 'PostHog']}
            alwaysOnLabel={c.alwaysOn}
            checked={analytics}
            onChange={setAnalytics}
          />
          <CategoryToggle
            title={c.marketing}
            description={c.marketingDesc}
            examples={['Meta Pixel + Conversions API', 'TikTok Pixel + Events API']}
            alwaysOnLabel={c.alwaysOn}
            checked={marketing}
            onChange={setMarketing}
          />

          <p className="type-caption mt-6 text-[var(--color-fg-subtle)]">
            {c.privacyPrefix}{' '}
            <Link
              href="/legal/aviso-de-privacidad"
              className="underline hover:text-[var(--color-accent)]"
            >
              {c.privacyLink}
            </Link>{' '}
            {c.privacyAnd}{' '}
            <Link href="/legal/cookies" className="underline hover:text-[var(--color-accent)]">
              {c.cookieLink}
            </Link>
            . {c.arcoPrefix}{' '}
            <Link href="/cuenta/privacidad" className="underline hover:text-[var(--color-accent)]">
              /cuenta/privacidad
            </Link>
            .
          </p>
        </div>

        <div className="flex flex-col gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface)] p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onRejectAll}
            className="type-nav h-10 rounded-sm border border-[var(--color-border-strong)] px-4 text-[var(--color-fg)] hover:border-[var(--color-accent)]"
          >
            {c.rejectAll}
          </button>
          <button
            type="button"
            onClick={save}
            className="type-nav h-10 rounded-sm border border-[var(--color-border-strong)] px-4 text-[var(--color-fg)] hover:border-[var(--color-accent)]"
          >
            {c.save}
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            className="type-nav h-10 rounded-sm bg-[var(--color-accent)] px-4 text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]"
          >
            {c.acceptAll}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryToggle({
  title,
  description,
  examples,
  alwaysOnLabel,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  examples: string[];
  alwaysOnLabel: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className={cn(
        'rounded-sm border p-4 transition-colors',
        checked
          ? 'border-[var(--color-accent)]/40 bg-[var(--color-accent)]/5'
          : 'border-[var(--color-border)]'
      )}
    >
      <label className="flex cursor-pointer items-start gap-3">
        <div className="mt-0.5">
          <div
            className={cn(
              'relative flex h-5 w-9 items-center rounded-full transition-colors',
              checked ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border-strong)]',
              disabled && 'opacity-60'
            )}
          >
            <span
              className={cn(
                'absolute h-4 w-4 rounded-full bg-[var(--color-fg)] transition-transform',
                checked ? 'translate-x-4' : 'translate-x-0.5'
              )}
            />
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="type-body-sm font-medium text-[var(--color-fg)]">{title}</span>
            {disabled ? (
              <span className="type-caption rounded-xs bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-[var(--color-fg-muted)]">
                {alwaysOnLabel}
              </span>
            ) : null}
          </div>
          <p className="type-caption mt-1 text-[var(--color-fg-muted)]">{description}</p>
          {examples.length > 0 ? (
            <p className="type-caption mt-2 font-mono text-[var(--color-fg-subtle)]">
              {examples.join(' · ')}
            </p>
          ) : null}
        </div>
      </label>
    </div>
  );
}
